import { AppBar, Box, Link, Stack, Tooltip, Typography, useMediaQuery } from '@mui/material';
import phoneIcon from 'assets/icons/phone.svg';
import { META } from 'common/constants';
import { PrimaryButton } from 'components/lib/PrimaryButton';
import { SecondaryButton } from 'components/lib/SecondaryButton';
import { SvgIconWrapper } from 'components/SvgIconWrapper';
import { currencyFormat } from 'helpers/utils';
import theme from 'providers/theme/theme';
import { type ElementRef, type FC, useEffect, useRef, useState } from 'react';
import { PaymentOptionSelect } from './components/PaymentOptionSelect';
import { QuoteCostDetails } from './components/QuoteCostDetails';
import {
  QuoteDetailsCardChildrenWrapperStyle,
  QuoteDetailsCardFooterStyle,
  QuoteDetailsCardHeaderStyle,
  QuoteDetailsCardHeaderWrapperStyle,
  QuoteDetailsCardPrimaryActionBtnStyle,
  QuoteDetailsCardStickyBarStyle,
  QuoteDetailsCardTopBarStyle,
  QuoteDetailsCardWrapperStyle,
} from './QuoteDetailsCard.styles';
import type { QuoteDetailsCardProps } from './QuoteDetailsCard.types';

const QuoteDetailsCard: FC<QuoteDetailsCardProps> = ({
  totalPayment,
  quoteId,
  monthlyPayment,
  headerContent,
  mainContent,
  isAnnualPaymentPlan,
  discountedTotalDue = 0,
  primaryActionBtnText,
  primaryAction,
  contentType,
  setPaymentOption,
  paymentOption,
  stickyBarContent,
  fullPaymentSavings = 1000,
  priceLoading = true,
  isViewOnly = false,
  isPrimaryActionDisabled = false,
  primaryActionDisabledMessage = '',
}) => {
  const isTableLayout = useMediaQuery(theme.breakpoints.down('md'));

  const [showSticky, setShowSticky] = useState(false);

  const QuoteDetailsCardRef = useRef<ElementRef<'div'>>(null);

  const toggleShowSticky = () => {
    const cardBottom = QuoteDetailsCardRef.current?.getBoundingClientRect().bottom;
    if (!cardBottom) return;

    setShowSticky(cardBottom < 0);
  };

  useEffect(() => {
    window.addEventListener('scroll', toggleShowSticky);

    return () => {
      window.removeEventListener('scroll', toggleShowSticky);
    };
  }, []);

  const payments = {
    totalPayment: currencyFormat(totalPayment!).merged,
    monthlyPayment: currencyFormat(monthlyPayment!).merged,
    fullPaymentSavings: fullPaymentSavings
      ? currencyFormat(fullPaymentSavings, 'USD', true).merged
      : '$0',
    discountedTotalDue: currencyFormat(discountedTotalDue!).merged,
  };

  const renderHeader = () => {
    switch (contentType) {
      case 'paymentDetails':
        return (
          <QuoteCostDetails
            monthlyPayment={payments.monthlyPayment}
            totalPayment={payments.totalPayment}
            discountedTotalDue={payments.discountedTotalDue}
            priceLoading={priceLoading}
            isAnnualPaymentPlan={isAnnualPaymentPlan}
          />
        );

      case 'paymentOptionSelect':
        return (
          <PaymentOptionSelect
            isViewOnly={isViewOnly}
            priceLoading={priceLoading}
            monthlyPayment={payments.monthlyPayment}
            totalPayment={payments.totalPayment}
            discountedTotalDue={payments.discountedTotalDue}
            paymentOption={paymentOption}
            setPaymentOption={setPaymentOption}
            fullPaymentSavings={payments.fullPaymentSavings}
          />
        );

      default:
        return <></>;
    }
  };

  return (
    <Box sx={QuoteDetailsCardWrapperStyle} ref={QuoteDetailsCardRef}>
      {!isTableLayout && <Box sx={QuoteDetailsCardTopBarStyle}>Quote Number {quoteId}</Box>}

      <Stack sx={QuoteDetailsCardHeaderStyle}>
        {!headerContent ? (
          <Stack sx={QuoteDetailsCardHeaderWrapperStyle}>
            {renderHeader()}

            {isTableLayout && (
              <Tooltip title={isPrimaryActionDisabled ? primaryActionDisabledMessage : ''}>
                <Box>
                  <PrimaryButton
                    onClick={primaryAction}
                    text={primaryActionBtnText}
                    disabled={isPrimaryActionDisabled}
                    sx={[
                      QuoteDetailsCardPrimaryActionBtnStyle,
                      priceLoading ? { pointerEvents: 'none' } : {},
                    ]}
                  />
                </Box>
              </Tooltip>
            )}
          </Stack>
        ) : (
          headerContent
        )}

        {showSticky && isTableLayout && (
          <AppBar component={Box} sx={QuoteDetailsCardStickyBarStyle} elevation={0}>
            {!stickyBarContent ? (
              <Stack
                justifyContent="space-between"
                width="100%"
                sx={{ paddingInline: '1.5rem', paddingBlock: '1rem' }}
              >
                <Tooltip title={isPrimaryActionDisabled ? primaryActionDisabledMessage : ''}>
                  <Box>
                    <PrimaryButton
                      onClick={primaryAction}
                      text={primaryActionBtnText}
                      disabled={isPrimaryActionDisabled}
                      sx={QuoteDetailsCardPrimaryActionBtnStyle}
                    />
                  </Box>
                </Tooltip>
              </Stack>
            ) : (
              stickyBarContent
            )}
          </AppBar>
        )}
      </Stack>

      {mainContent && <Stack sx={QuoteDetailsCardChildrenWrapperStyle}>{mainContent}</Stack>}

      <Stack sx={QuoteDetailsCardFooterStyle}>
        <Typography>Questions? Live chat or call our priority line.</Typography>

        <SecondaryButton
          data-test="call-priority-line"
          component={Link}
          href={`tel:${META.PRIORITY_CONTACT_NUMBER}`}
          startAdornment={<SvgIconWrapper src={phoneIcon} />}
          text={
            <Typography component="span" variant="h2">
              {META.PRIORITY_CONTACT_NUMBER}
            </Typography>
          }
        />
      </Stack>
    </Box>
  );
};

export default QuoteDetailsCard;
